import type { TranslatableString } from '@geoic/shared';
import { api, type ListResult } from '@/lib/api-client.js';

export type MediaItem = {
  _id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  width: number | null;
  height: number | null;
  folderId: string | null;
  alt: TranslatableString;
  urls: {
    original: string;
    large: string | null;
    medium: string | null;
    square: string | null;
    thumb: string | null;
  };
  blurDataUrl: string | null;
  createdAt: string;
};

export type MediaFolder = { _id: string; name: string; parentId: string | null };

export type MediaUsage = { collectionName: string; docId: string; field: string };

export type MediaListParams = {
  page?: number;
  limit?: number;
  folderId?: string;
  kind?: 'image' | 'file';
  search?: string;
};

export const mediaApi = {
  list: (params: MediaListParams) => api.get<ListResult<MediaItem>>('/media', params),

  getOne: (id: string) => api.get<{ data: MediaItem }>(`/media/${id}`),

  folders: () => api.get<{ data: MediaFolder[] }>('/media/folders'),

  createFolder: (name: string, parentId: string | null) =>
    api.post<{ data: MediaFolder }>('/media/folders', { name, parentId }),

  usages: (id: string) => api.get<{ data: MediaUsage[] }>(`/media/${id}/usages`),

  update: (id: string, body: { alt?: TranslatableString; folderId?: string | null }) =>
    api.patch<{ data: MediaItem }>(`/media/${id}`, body),

  /** `force` — fayl ishlatilayotgan bo'lsa ham o'chirish (tasdiqdan keyin). */
  remove: (id: string, force = false) =>
    api.delete<void>(`/media/${id}`, force ? { force: 'true' } : undefined),

  /**
   * Ko'p fayl yuklash.
   *
   * Barchasi BITTA so'rovda ketadi: server ularni ketma-ket qayta ishlaydi
   * (har bir rasm uchun 5 variant — CPU og'ir ish, 2 GB serverda parallel
   * bajarish xavfli).
   */
  upload: (files: readonly File[], folderId: string | null) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    if (folderId) form.append('folderId', folderId);

    return api.upload<{ data: MediaItem[] }>('/media', form);
  },
};

/** «pages» → «Sahifalar» — «qayerda ishlatilgan» ro'yxati uchun. */
export const COLLECTION_LABELS: Record<string, string> = {
  pages: 'Sahifalar',
  news: 'Yangiliklar',
  staff: 'Rahbariyat',
  documents: 'Hujjatlar',
  services: 'Xizmatlar',
  projects: 'Loyihalar',
  courses: 'Kurslar',
  products: 'Mahsulotlar',
  productCategories: 'Kategoriyalar',
  brands: 'Brendlar',
  droneShowPackages: 'Dron-shou paketlari',
  droneShowProjects: 'Dron-shou portfolio',
  settings: 'Sozlamalar',
};

export function collectionLabel(name: string): string {
  return COLLECTION_LABELS[name] ?? name;
}
